import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import BusinessIcon from "@mui/icons-material/Business";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import DescriptionIcon from "@mui/icons-material/Description";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import VerifiedIcon from "@mui/icons-material/Verified";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Grid,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { useEffect, useState } from "react";
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";
import StatusChip from "../components/StatusChip";
import publicacaoService from "../services/publicacao";
import { formatDateToShow } from "../utils/formatters";
import { getApiErrorMessage } from "../utils/getApiErrorMessage";

function InfoField({ label, value, icon: Icon }) {
  const theme = useTheme();
  const display = value || "—";
  const empty = !value;

  return (
    <Box
      sx={{
        p: 2,
        height: "100%",
        borderRadius: 2,
        border: "1px solid",
        borderColor: "divider",
        bgcolor: (t) =>
          alpha(t.palette.background.default, t.palette.mode === "dark" ? 0.35 : 0.6),
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
        {Icon && (
          <Icon
            sx={{ fontSize: 18, color: theme.palette.primary.main, opacity: 0.9 }}
          />
        )}
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ textTransform: "uppercase", letterSpacing: 0.6, fontWeight: 600 }}
        >
          {label}
        </Typography>
      </Stack>
      <Typography
        variant="body1"
        sx={{
          fontWeight: empty ? 400 : 600,
          color: empty ? "text.disabled" : "text.primary",
          wordBreak: "break-word",
        }}
      >
        {display}
      </Typography>
    </Box>
  );
}

function SectionTitle({ children }) {
  return (
    <Typography
      variant="subtitle2"
      color="text.secondary"
      sx={{
        mb: 1.5,
        fontWeight: 700,
        letterSpacing: 0.4,
        textTransform: "uppercase",
      }}
    >
      {children}
    </Typography>
  );
}

export default function PublicacaoDetalhe() {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let ativo = true;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const data = await publicacaoService.obterPublicaPorId(id);
        if (ativo) setItem(data);
      } catch (err) {
        if (ativo) {
          setError(
            getApiErrorMessage(err, "Erro ao carregar detalhes da publicação."),
          );
        }
      } finally {
        if (ativo) setLoading(false);
      }
    })();
    return () => {
      ativo = false;
    };
  }, [id]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate("/publicacoes")}>
          Voltar
        </Button>
      </Container>
    );
  }

  if (!item) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="info">Publicação não encontrada.</Alert>
      </Container>
    );
  }

  const situacaoDisponivel =
    item.statusSolicitacao !== null &&
    item.statusSolicitacao !== undefined &&
    item.statusSolicitacao !== "";

  return (
    <Container maxWidth="md" sx={{ py: { xs: 3, md: 5 } }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate("/publicacoes")}
        sx={{ mb: 2.5 }}
      >
        Voltar para publicações
      </Button>

      <Paper
        elevation={0}
        sx={{
          overflow: "hidden",
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 3,
        }}
      >
        <Box
          sx={{
            px: { xs: 2.5, sm: 3.5 },
            py: { xs: 2.5, sm: 3 },
            background: `linear-gradient(135deg, ${alpha(
              theme.palette.primary.main,
              theme.palette.mode === "dark" ? 0.22 : 0.12,
            )} 0%, ${alpha(theme.palette.background.paper, 0.2)} 100%)`,
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", sm: "center" }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="overline"
                color="text.secondary"
                sx={{ letterSpacing: 1 }}
              >
                Publicação no site
              </Typography>
              <Typography
                variant="h4"
                component="h1"
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: "1.5rem", sm: "1.85rem" },
                  lineHeight: 1.25,
                  wordBreak: "break-word",
                }}
              >
                {item.empreendimentoNome || "Empreendimento não informado"}
              </Typography>
              {item.tipoLicencaNome && (
                <Chip
                  size="small"
                  icon={<DescriptionIcon />}
                  label={item.tipoLicencaNome}
                  sx={{ mt: 1.5, fontWeight: 600 }}
                  color="primary"
                  variant="outlined"
                />
              )}
            </Box>

            {situacaoDisponivel ? (
              <StatusChip status={item.statusSolicitacao} />
            ) : (
              <Chip label="Situação não informada" size="small" variant="outlined" />
            )}
          </Stack>
        </Box>

        <Box sx={{ px: { xs: 2.5, sm: 3.5 }, py: { xs: 2.5, sm: 3.5 } }}>
          <SectionTitle>Identificação</SectionTitle>
          <Grid container spacing={1.5} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6}>
              <InfoField
                label="Empreendimento"
                value={item.empreendimentoNome}
                icon={BusinessIcon}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <InfoField
                label="CPF/CNPJ"
                value={item.empreendimentoCnpjCpf}
                icon={VerifiedIcon}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <InfoField
                label="Município que licenciou"
                value={item.municipioQueLicenciou}
                icon={LocationOnIcon}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <InfoField
                label="Tipo de processo"
                value={item.tipoLicencaNome}
                icon={DescriptionIcon}
              />
            </Grid>
          </Grid>

          <Divider sx={{ mb: 3 }} />

          <SectionTitle>Datas do processo</SectionTitle>
          <Grid container spacing={1.5} sx={{ mb: item.licencaEmitida ? 3 : 0 }}>
            <Grid item xs={12} sm={4}>
              <InfoField
                label="Abertura da solicitação"
                value={formatDateToShow(item.dataAberturaSolicitacao)}
                icon={CalendarMonthIcon}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <InfoField
                label="Data de emissão"
                value={
                  item.dataEmissaoLicenca
                    ? formatDateToShow(item.dataEmissaoLicenca)
                    : null
                }
                icon={CalendarMonthIcon}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <InfoField
                label="Validade"
                value={
                  item.validadeLicenca
                    ? formatDateToShow(item.validadeLicenca)
                    : null
                }
                icon={CalendarMonthIcon}
              />
            </Grid>
          </Grid>

          {item.licencaEmitida && (
            <>
              <Divider sx={{ mb: 3 }} />
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                alignItems={{ xs: "stretch", sm: "center" }}
                justifyContent="space-between"
                sx={{
                  p: 2,
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: alpha(theme.palette.success.main, 0.35),
                  bgcolor: alpha(
                    theme.palette.success.main,
                    theme.palette.mode === "dark" ? 0.12 : 0.08,
                  ),
                }}
              >
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    Documento da licença disponível
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Consulte a autenticidade do documento emitido para esta
                    publicação.
                  </Typography>
                </Box>
                <Button
                  component={RouterLink}
                  to={`/validar/licenca/${item.solicitacaoDeLicencaId}`}
                  variant="contained"
                  color="success"
                  endIcon={<OpenInNewIcon />}
                  sx={{ whiteSpace: "nowrap", flexShrink: 0 }}
                >
                  Validar documento
                </Button>
              </Stack>
            </>
          )}
        </Box>
      </Paper>
    </Container>
  );
}
